import { Component, OnInit, OnDestroy, AfterViewInit, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToastService } from '../../services/toast.service';
import { LanguageService, Language } from '../../services/language.service';
import { Subject, takeUntil, combineLatest } from 'rxjs';

interface FAQ {
  question: string;
  answer: string;
  isOpen: boolean;
}

@Component({
  selector: 'app-contact-us',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './contact-us.html',
  styleUrls: ['./contact-us.scss']
})
export class ContactUsComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('contactInfoBlock', { static: false }) contactInfoBlock?: ElementRef;
  
  contactForm!: FormGroup;
  isSubmitting = false;
  availableLanguages: Language[] = [];
  
  private destroy$ = new Subject<void>();
  
  faqs: FAQ[] = [];

  constructor(
    private formBuilder: FormBuilder,
    private toastService: ToastService,
    private languageService: LanguageService
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.updateFAQs();
    
    // Subscribe to both language and translations changes
    // This ensures FAQs update when translations load after page refresh
    combineLatest([
      this.languageService.currentLanguage$,
      this.languageService.translations$
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateFAQs();
      });
  }

  ngAfterViewInit(): void {
    // Remove underlines from phone number links after view initializes
    this.removeUnderlinesFromLinks();
    
    // Also remove after translations load
    combineLatest([
      this.languageService.currentLanguage$,
      this.languageService.translations$
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        setTimeout(() => this.removeUnderlinesFromLinks(), 100);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private removeUnderlinesFromLinks(): void {
    setTimeout(() => {
      const infoContentElements = document.querySelectorAll('.contact-us-container .info-content');
      infoContentElements.forEach((element) => {
        const links = element.querySelectorAll('a');
        links.forEach((link) => {
          link.style.setProperty('text-decoration', 'none', 'important');
          link.style.setProperty('text-decoration-line', 'none', 'important');
          link.style.setProperty('text-decoration-style', 'none', 'important');
        });
      });
    }, 0);
  }

  private updateFAQs(): void {
    this.faqs = [
      {
        question: this.languageService.t('contactFaq_q1'),
        answer: this.languageService.t('contactFaq_a1'),
        isOpen: false
      },
      {
        question: this.languageService.t('contactFaq_q2'),
        answer: this.languageService.t('contactFaq_a2'),
        isOpen: false
      },
      {
        question: this.languageService.t('contactFaq_q3'),
        answer: this.languageService.t('contactFaq_a3'),
        isOpen: false
      },
      {
        question: this.languageService.t('contactFaq_q4'),
        answer: this.languageService.t('contactFaq_a4'),
        isOpen: false
      },
      {
        question: this.languageService.t('contactFaq_q5'),
        answer: this.languageService.t('contactFaq_a5'),
        isOpen: false
      }
    ];
  }

  private initForm(): void {
    this.contactForm = this.formBuilder.group({
      firstName: ['', [Validators.required, Validators.minLength(2)]],
      lastName: ['', [Validators.required, Validators.minLength(2)]],
      email: ['', [Validators.required, Validators.email]],
      phone: [''],
      subject: ['', Validators.required],
      message: ['', [Validators.required, Validators.minLength(10)]]
    });
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.contactForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  async onSubmit(): Promise<void> {
    if (this.contactForm.invalid) {
      this.markFormGroupTouched();
      return;
    }

    this.isSubmitting = true;

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Show success message
      this.toastService.success('Thank you for your message! We\'ll get back to you within 24 hours.');
      
      // Reset form
      this.contactForm.reset();
      this.contactForm.markAsUntouched();
      this.contactForm.markAsPristine();
      
    } catch (error) {
      this.toastService.error('Sorry, there was an error sending your message. Please try again.');
    } finally {
      this.isSubmitting = false;
    }
  }

  private markFormGroupTouched(): void {
    Object.keys(this.contactForm.controls).forEach(key => {
      const control = this.contactForm.get(key);
      control?.markAsTouched();
    });
  }

  toggleFaq(index: number): void {
    this.faqs[index].isOpen = !this.faqs[index].isOpen;
  }

  // Helper methods for translations
  getTranslation(key: string): string {
    return this.languageService.t(key);
  }
}
